import { isVerifiedTrainer, vtHtFromCoachTtc } from "./VerifiedTrainerAmounts";

/**
 * TVA comprise dans la part du prof, pour les trois marques, telle que la facture la calcule et que le backend
 * l'enregistre sur la charge dès la déclaration (TeachrVatCalculator, bloc `teachrVat` de l'API).
 *
 * Teach'r et Déclarons : la part du prof est un montant TTC, la TVA d'un prof assujetti est comprise dedans,
 * jamais ajoutée par-dessus (TVA = taux % de la part TTC : 105 € à 20 % → 84 € HT et 21 € de TVA).
 * Verified Trainer et profs Teach'r en coopérative (depuis le 1er novembre 2026) : la TVA s'ajoute par-dessus la
 * part HT, voir VerifiedTrainerAmounts ; ici, part HT = part TTC ÷ (1 + taux) (118,50 € → 98,75 € HT et 19,75 € de TVA).
 */

type TeachrRates = {
  defaultFees?: string | number | null;
  tauxTva?: string | number | null;
} | null | undefined;

const toNumber = (value: unknown): number => {
  const n = typeof value === "number" ? value : parseFloat(String(value ?? ""));
  return Number.isFinite(n) ? n : 0;
};

/** Taux de TVA du prof, en pourcentage (0 s'il n'est pas assujetti). */
export const coachVatRate = (teachr: TeachrRates): number => toNumber(teachr?.tauxTva);

/** Le prof est assujetti à la TVA : taux non nul dans ses paramètres de facturation. */
export const coachIsSubjectToVat = (teachr: TeachrRates): boolean => coachVatRate(teachr) > 0;

/**
 * Part TTC du prof pour une déclaration : prix horaire payé par le client × 2 × (1 − frais) × heures, soit
 * teachr_price × heures côté API, la même règle pour les trois marques.
 */
export const coachShareTtc = (hourlyPaid: unknown, hours: unknown, teachr: TeachrRates): number =>
  toNumber(hourlyPaid) * 2 * (1 - toNumber(teachr?.defaultFees)) * toNumber(hours);

/** Part hors TVA contenue dans un montant TTC du prof, selon la règle de la facture de la marque active. */
export const coachHtFromTtc = (coachTtc: number, teachr: TeachrRates): number =>
  isVerifiedTrainer() ? vtHtFromCoachTtc(coachTtc, teachr) : coachTtc * (1 - coachVatRate(teachr) / 100);

/** TVA comprise dans un montant TTC du prof (0 s'il n'est pas assujetti). */
export const coachVatIncluded = (coachTtc: number, teachr: TeachrRates): number =>
  coachTtc - coachHtFromTtc(coachTtc, teachr);

/** Libellé de la rémunération : précise « TTC » quand le prof est assujetti à la TVA. */
export const coachRemunerationLabel = (teachr: TeachrRates, base = "Rémunération"): string =>
  coachIsSubjectToVat(teachr) ? `${base} TTC` : base;
